import { ReactNode } from 'react';
import {
  createUnifiedTheme,
  genPageTheme,
  pageTheme,
  palettes,
  shapes,
  UnifiedThemeProvider,
} from '@backstage/theme';
import { AppTheme } from '@backstage/core-plugin-api';

/**
 * Colors from the UT Austin visual identity guidelines:
 * https://umac.utexas.edu/brand-center/visual-identity/
 */
const ut = {
  burntOrange: '#bf5700',
  white: '#ffffff',
  darkGray: '#333f48',
  gold: '#f8971f',
  warmTan: '#d6d2c4',
  darkTeal: '#005f86',
  cyan: '#00a9b7',
};

// Libre Franklin is the web replacement for Benton Sans. It is bundled with the
// app (see index.tsx) so no request leaves for a font host.
const fontFamily = '"Libre Franklin", Arial, sans-serif';

// Header banners: burnt orange into dark gray, white text stays readable on both.
const headerTheme = genPageTheme({
  colors: [ut.burntOrange, ut.darkGray],
  shape: shapes.wave,
});
const pageThemes = Object.fromEntries(
  Object.keys(pageTheme).map(key => [key, headerTheme]),
);

// White on burnt orange is 4.6:1 and burnt orange on dark gray is only 2.4:1,
// so the dark theme uses gold (4.9:1 on dark gray) wherever orange would be text.
const utLight = createUnifiedTheme({
  fontFamily,
  defaultPageTheme: 'home',
  pageTheme: pageThemes,
  palette: {
    ...palettes.light,
    primary: { main: ut.burntOrange },
    secondary: { main: ut.darkTeal },
    link: ut.darkTeal,
    linkHover: ut.burntOrange,
    tabbar: { indicator: ut.burntOrange },
    navigation: {
      ...palettes.light.navigation,
      background: ut.darkGray,
      indicator: ut.gold,
      color: ut.warmTan,
      selectedColor: ut.white,
      navItem: { hoverBackground: '#425059' },
      submenu: { background: '#2a343b' },
    },
  },
});

const utDark = createUnifiedTheme({
  fontFamily,
  defaultPageTheme: 'home',
  pageTheme: pageThemes,
  palette: {
    ...palettes.dark,
    primary: { main: ut.burntOrange },
    secondary: { main: ut.cyan },
    background: { default: '#262f35', paper: ut.darkGray },
    link: ut.gold,
    linkHover: ut.warmTan,
    tabbar: { indicator: ut.gold },
    navigation: {
      ...palettes.dark.navigation,
      background: '#1d252a',
      indicator: ut.gold,
      color: ut.warmTan,
      selectedColor: ut.white,
      navItem: { hoverBackground: '#2c373e' },
      submenu: { background: '#161d21' },
    },
  },
});

export const utThemes: AppTheme[] = [
  {
    id: 'ut-light',
    title: 'UT Light',
    variant: 'light',
    Provider: ({ children }: { children: ReactNode }) => (
      <UnifiedThemeProvider theme={utLight} children={children} />
    ),
  },
  {
    id: 'ut-dark',
    title: 'UT Dark',
    variant: 'dark',
    Provider: ({ children }: { children: ReactNode }) => (
      <UnifiedThemeProvider theme={utDark} children={children} />
    ),
  },
];
